import EcoImg from 'eco-design/base/eco-img';
import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';
import { useLang } from '@/app/utils/useLang';
import { fetchPost } from 'lowcode/utils/service/FetchClient';
import { useRouter } from 'next/router';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import EditorLink from 'lowcode/components/EditorLink';

interface ModalProps {
    visible: boolean;
    onClose: () => void;
    title?: string;
    children?: React.ReactNode;
}

const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 6L18 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const linkMap = {
    eu: {
        privacy: 'https://eu.ecoflow.com/policies/privacy-policy',
        terms: 'https://eu.ecoflow.com/policies/terms-of-service',
    },
    uk: {
        privacy: 'https://uk.ecoflow.com/policies/privacy-policy',
        terms: 'https://uk.ecoflow.com/policies/terms-of-service',
    },
    de: {
        privacy: 'https://de.ecoflow.com/policies/privacy-policy',
        terms: 'https://de.ecoflow.com/policies/terms-of-service',
    },
    fr: {
        privacy: 'https://fr.ecoflow.com/policies/privacy-policy',
        terms: 'https://fr.ecoflow.com/policies/terms-of-service',
    },
    es: {
        privacy: 'https://es.ecoflow.com/policies/privacy-policy',
        terms: 'https://es.ecoflow.com/policies/terms-of-service',
    },
    it: {
        privacy: 'https://it.ecoflow.com/policies/privacy-policy',
        terms: 'https://it.ecoflow.com/policies/terms-of-service',
    },
};

const Modal: FC<ModalProps> = ({ visible, onClose, title, children }) => {
    if (!visible) return null;

    const { t } = useLang('stream_ultra_x');
    const { locale = 'eu' } = useRouter();

    const [input, setInput] = useState('');
    const [tip, setTip] = useState<string>('');
    const [isAgreed, setIsAgreed] = useState(false);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };
    const handleFocus = () => {
        if (tip) setTip('');
    };
    const handleBlur = () => {
        const email = input.trim();
        if (!email) {
            setTip(t('A2_subscribe_empty'));
            return;
        }
        const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!regex.test(email)) {
            setTip(t('A2_subscribe_error'));
            return;
        }
        if (!isAgreed) {
            setTip(t('A2_subscribe_check'));
            return;
        }
        return true;
    };
    const handleClick = async () => {
        if (!handleBlur()) return;

        const email = input.trim();
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const configs = {
            eu: {
                contactListId: ['854680147'],
                fields: [
                    { id: '4427', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '7596', type: 'multiple', value: ['55'], overwrite: false },
                    { id: '13666', type: 'text', value: dateString, overwrite: false },
                ],
            },

            uk: {
                contactListId: ['555078618'],
                fields: [
                    { id: '4430', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '7596', type: 'multiple', value: ['55'], overwrite: false },
                    { id: '13666', type: 'text', value: dateString, overwrite: false },
                ],
            },
            de: {
                contactListId: ['2060409631'],
                fields: [
                    { id: '4425', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '7596', type: 'multiple', value: ['55'], overwrite: false },
                    { id: '13666', type: 'text', value: dateString, overwrite: false },
                ],
            },
            fr: {
                contactListId: ['1967314273'],
                fields: [
                    { id: '4428', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '7596', type: 'multiple', value: ['55'], overwrite: false },
                    { id: '13666', type: 'text', value: dateString, overwrite: false },
                ],
            },
            es: {
                contactListId: ['1512016382'],
                fields: [
                    { id: '4426', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '7596', type: 'multiple', value: ['55'], overwrite: false },
                    { id: '13666', type: 'text', value: dateString, overwrite: false },
                ],
            },
            it: {
                contactListId: ['327038689'],
                fields: [
                    { id: '4429', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: true },
                    { id: '7596', type: 'multiple', value: ['55'], overwrite: false },
                    { id: '13666', type: 'text', value: dateString, overwrite: false },
                ],
            },
        };
        const { contactListId, fields } = configs[locale as keyof typeof configs] || configs['eu'];

        const res = await fetchPost(
            `${baseUrl(locale)}/subscribe/new`,
            locale,
            {
                contactListId: contactListId[0],
                email,
                fields,
                // source: 'Email Subscribers from Bosch_2025',
                otherContactListIds: contactListId.slice(1),
            },
            {
                headers: {
                    // apiKey: '527760f6df93c0d6e98609fa8e8a1f4a',
                },
            },
        );

        if (res?.code === '0') {
            setTip(t('A2_subscribe_success'));
            setInput('');
            setTimeout(() => {
                setTip('');
            }, 5000);
        } else {
            setTip(t('A2_subscribe_fail'));
            console.error(res?.message);
        }
    };

    return createPortal(
        <div className={styles.modal}>
            <div className="modal-overlay" onClick={onClose} />
            <div className="modal-container">
                {/* <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>
                <div className="modal-content">{children}</div>
                <div className="background-img">
                    <EcoImg
                        pc="https://websiteoss.ecoflow.com/upload/image/1753671650520/pc.png"
                        pad="https://websiteoss.ecoflow.com/upload/image/1753671650737/pad.png"
                        h5="https://websiteoss.ecoflow.com/upload/image/1753671650349/h5.png"
                    />
                </div> */}
                <button className="modal-close" onClick={onClose}>
                    <CloseIcon />
                </button>
                <div className="title">{t('A2_subscribe_title')}</div>
                <div className="desc">{t('A2_subscribe_desc')}</div>

                <div className="subscribe-wrapper">
                    {/* <div className="subscribe-title">{t('A2_subscribe_title_title')}</div> */}

                    <div className="subscribe-input-wrapper">
                        <input
                            type="text"
                            placeholder={t('A2_subscribe_placeholder')}
                            className="search-input"
                            value={input}
                            onChange={handleInput}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <button className={`search-button ${input && 'active'}`} onClick={handleClick}>
                            {t('A2_subscribe_button')}
                        </button>
                    </div>
                    <div className={`subscribe-tip ${t('A2_subscribe_success') === tip && 'success'}`}>{tip}</div>
                    {/* <div className="footer-text">{`${t('A2_subscribe_footer_text')}`}</div> */}
                    <div className="agreement-wrapper">
                        <input type="checkbox" id="agreement" checked={isAgreed} onChange={(e) => setIsAgreed(e.target.checked)} />
                        <div className="footer-text">
                            {t.rich('A2_subscribe_footer_text', {
                                privacy: (tx: string) => (
                                    <EditorLink href={linkMap[locale as keyof typeof linkMap].privacy} className="agreement-link" target="_blank">
                                        {tx}
                                    </EditorLink>
                                ),
                                terms: (tx: string) => (
                                    <EditorLink href={linkMap[locale as keyof typeof linkMap].terms} className="agreement-link" target="_blank">
                                        {tx}
                                    </EditorLink>
                                ),
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default Modal;
